import { useCallback } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'generated/graphql-gateway';
import { getStaticPathname } from 'navigation/location/location';
import { appendObjectToQueryString } from 'navigation/utils';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useOrderContext } from 'state/order';
import { ISelectOrderStore } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { routes } from 'utils/routing';

import { useDefaultServiceMode } from './use-default-service-mode';

export function useSelectStore() {
  const {
    selectOrderStore,
    reorder: { pendingReorder, setShouldHandleReorder },
  } = useOrderContext();
  const { redirectUrl, targetItemIds } = useLocalSearchParams<{
    redirectUrl?: string;
    targetItemIds?: string | string[];
  }>();

  const { selectServiceMode } = useServiceModeContext();
  const router = useRouter();

  const getDefaultServiceMode = useDefaultServiceMode();

  const dismissStoreLocator = useCallback(() => {
    const isStoreInfoModal = getStaticPathname().startsWith(routes.store);
    if (isStoreInfoModal) {
      router.dismiss();
      router.back();
    } else if (redirectUrl) {
      // Store-locator search params may include `targetItemIds` (e.g. menu deep links). The
      // dismiss target is only `redirectUrl` (path), so re-attach the param here to keep
      // promoted/pinned ordering after picking a store.
      const normalizedTargetItemIds = Array.isArray(targetItemIds)
        ? targetItemIds.join(',')
        : targetItemIds?.trim();
      const dismissHref = normalizedTargetItemIds
        ? appendObjectToQueryString(redirectUrl, { targetItemIds: normalizedTargetItemIds })
        : redirectUrl;
      router.dismissTo(dismissHref);
    } else {
      // Avoid dismissing arbitrary modals when selection is triggered outside the
      // store locator flow (e.g., deep links/autoselect). Let the caller control navigation.
      return;
    }
  }, [redirectUrl, router, targetItemIds]);

  const selectStoreCallback = useCallback(
    async ({ newStore, serviceMode }: { newStore: IRestaurant; serviceMode: ServiceMode }) => {
      dismissStoreLocator();
      // TODO: We probably want to use dynamic event name here
      logEvent(CustomEventNames.ORDER_HERE_STORE_CONFIRMATION_FLOW, EventTypes.Navigation, {
        'Restaurant Id': newStore._id,
        'Restaurant Name': newStore.name,
      });
      // set service mode to ensure we aren't taken back to static menu
      // need to do this after previous checks so that we don't set service mode
      // when enableOrdering is false
      selectServiceMode(serviceMode);

      // if user has a pending reorder return and let the useEffect handle it below
      if (pendingReorder) {
        setShouldHandleReorder(true);
        return;
      }

      syncStorage.setBoolean(SyncStorageKey.SKIP_AUTO_SELECT_PROMPT, true);
    },
    [selectServiceMode, pendingReorder, dismissStoreLocator, setShouldHandleReorder]
  );

  return useCallback(
    async ({
      newStore,
      requestedServiceMode,
      skipChangeStoreMessage,
      callback,
    }: ISelectOrderStore) => {
      const defaultServiceMode = getDefaultServiceMode(newStore);
      const finalServiceMode = requestedServiceMode ?? defaultServiceMode;

      await selectOrderStore({
        newStore,
        requestedServiceMode: finalServiceMode,
        skipChangeStoreMessage,
        callback: () => {
          selectStoreCallback({
            newStore,
            serviceMode: finalServiceMode,
          });
          callback?.();
        },
      });
    },
    [getDefaultServiceMode, selectOrderStore, selectStoreCallback]
  );
}
